'use client';

import { IS_DEV } from '@/lib/utils';

/** Mirrors `sceneStore.isPlayModeStarting` for dist-refresh coordination without importing the store barrel. */
let playModeStartingForDistRefresh = false;

/** Keep in sync with {@link SceneStore.setPlayModeStarting}. */
export function syncPlayModeStartingGate(starting: boolean): void {
  playModeStartingForDistRefresh = starting;
}

function isPlayModeStartingForDistRefresh(): boolean {
  return playModeStartingForDistRefresh;
}

/** Wait for build-stamp / related writes to settle before refreshing. */
const BUILD_STAMP_DEBOUNCE_MS = 800;
/** Skip watcher-triggered refresh shortly after `refreshAfterBuild` (e.g. from `buildProject`) to avoid double work. */
const WATCH_COOLDOWN_AFTER_REFRESH_MS = 2500;
const DEBUG_LOG = {
  queue: false && IS_DEV,
} as const;

let distDebounceTimer: ReturnType<typeof setTimeout> | null = null;
let queuedDistRefreshDrainTimer: ReturnType<typeof setTimeout> | null = null;
/** Set when `refreshAfterBuild` finishes (build button or watcher). */
let lastRefreshAfterBuildCompletedAt = 0;
/** True while `refreshAfterBuild` is actively running (prevents watcher scheduling a concurrent refresh). */
let refreshAfterBuildInProgress = false;
/** Merged pending refresh callback captured while watcher refresh is suppressed. */
let queuedDistRefreshCallback: (() => void) | null = null;

type DistRefreshScheduleDecision = 'runNow' | 'queue';

function getCooldownRemainingMs(now: number = Date.now()): number {
  return Math.max(0, WATCH_COOLDOWN_AFTER_REFRESH_MS - (now - lastRefreshAfterBuildCompletedAt));
}

function shouldSuppressWatcherRefresh(now: number = Date.now()): boolean {
  return refreshAfterBuildInProgress
    || getCooldownRemainingMs(now) > 0
    || isPlayModeStartingForDistRefresh();
}

function getDistRefreshScheduleDecision(): DistRefreshScheduleDecision {
  if (shouldSuppressWatcherRefresh()) {
    return 'queue';
  }
  return 'runNow';
}

function clearQueuedDistRefreshDrainTimer(): void {
  if (queuedDistRefreshDrainTimer != null) {
    clearTimeout(queuedDistRefreshDrainTimer);
    queuedDistRefreshDrainTimer = null;
  }
}

function queueDistRefresh(onFire: () => void): void {
  // Keep only the latest callback reference; repeated events are merged.
  queuedDistRefreshCallback = onFire;
  if (DEBUG_LOG.queue) {
    console.debug('[DistRefreshCoordination] Queued watcher refresh request');
  }
  scheduleQueuedDistRefreshDrain();
}

function scheduleQueuedDistRefreshDrain(): void {
  if (queuedDistRefreshCallback == null) {
    clearQueuedDistRefreshDrainTimer();
    return;
  }
  if (queuedDistRefreshDrainTimer != null) {
    return;
  }
  const waitMs = refreshAfterBuildInProgress ? 250 : getCooldownRemainingMs();
  queuedDistRefreshDrainTimer = setTimeout(() => {
    queuedDistRefreshDrainTimer = null;
    flushQueuedDistRefreshIfReady();
  }, waitMs);
}

function scheduleDebouncedDistRefresh(onFire: () => void): void {
  if (distDebounceTimer != null) {
    clearTimeout(distDebounceTimer);
  }
  distDebounceTimer = setTimeout(() => {
    distDebounceTimer = null;
    const decision = getDistRefreshScheduleDecision();
    if (decision === 'runNow') {
      onFire();
      return;
    }
    if (decision === 'queue') {
      queueDistRefresh(onFire);
    }
  }, BUILD_STAMP_DEBOUNCE_MS);
}

/** Clears a pending watcher-only refresh (call before `refreshAfterBuild` from `buildProject`). */
export function cancelPendingDistRefresh(): void {
  if (distDebounceTimer != null) {
    clearTimeout(distDebounceTimer);
    distDebounceTimer = null;
  }
  queuedDistRefreshCallback = null;
  clearQueuedDistRefreshDrainTimer();
}

/** Call when `refreshAfterBuild` starts so watcher events during the refresh are suppressed. */
export function markRefreshAfterBuildStarted(): void {
  refreshAfterBuildInProgress = true;
  scheduleQueuedDistRefreshDrain();
}

/** Call when `refreshAfterBuild` completes so build-stamp events do not immediately schedule another refresh. */
export function markDistRefreshAfterBuildCompleted(): void {
  refreshAfterBuildInProgress = false;
  lastRefreshAfterBuildCompletedAt = Date.now();
  scheduleQueuedDistRefreshDrain();
}

/**
 * Debounced callback for watcher-driven `.editor/build-stamp.json` changes.
 */
export function scheduleDistRefreshCallback(onFire: () => void): void {
  const decision = getDistRefreshScheduleDecision();
  if (decision === 'runNow') {
    scheduleDebouncedDistRefresh(onFire);
    return;
  }
  if (decision === 'queue') {
    queueDistRefresh(onFire);
  }
}

/**
 * Replays one queued watcher refresh once suppression gates are open.
 * Returns true when the queued refresh was scheduled to run.
 */
export function flushQueuedDistRefreshIfReady(): boolean {
  if (queuedDistRefreshCallback == null) {
    return false;
  }
  const decision = getDistRefreshScheduleDecision();
  if (decision !== 'runNow') {
    scheduleQueuedDistRefreshDrain();
    return false;
  }
  const onFire = queuedDistRefreshCallback;
  queuedDistRefreshCallback = null;
  if (DEBUG_LOG.queue) {
    console.debug('[DistRefreshCoordination] Draining queued watcher refresh request');
  }
  scheduleDebouncedDistRefresh(onFire);
  return true;
}


